import React, { useState, useEffect } from 'react';
import {
    fetchAllSocialLinks,
    createSocialLink,
    updateSocialLink,
    toggleSocialLink,
    deleteSocialLink,
} from '../../services/apiService';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'bootstrap/dist/css/bootstrap.min.css';

const EMPTY_FORM = { platform: '', url: '', icon: '', name: '', isActive: true, order: 0 };

const SocialMedia = () => {
    const token = localStorage.getItem('accessToken');

    const [links, setLinks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(null);

    const [showModal, setShowModal] = useState(false);
    const [editTarget, setEditTarget] = useState(null); // null = create, object = edit
    const [form, setForm] = useState(EMPTY_FORM);

    const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const res = await fetchAllSocialLinks(token);
            setLinks(res.data || []);
        } catch {
            toast.error('Failed to load social links');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const openCreate = () => {
        setEditTarget(null);
        setForm(EMPTY_FORM);
        setShowModal(true);
    };

    const openEdit = (link) => {
        setEditTarget(link);
        setForm({
            platform: link.platform || '',
            url: link.url || '',
            icon: link.icon || '',
            name: link.name || '',
            isActive: link.isActive ?? true,
            order: link.order ?? 0,
        });
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditTarget(null);
        setForm(EMPTY_FORM);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!form.platform.trim() || !form.url.trim()) {
            toast.error('Platform and URL are required');
            return;
        }
        setSaving(true);
        try {
            const payload = {
                platform: form.platform.trim(),
                url: form.url.trim(),
                icon: form.icon.trim() || null,
                name: form.name.trim() || null,
                isActive: form.isActive,
                order: Number(form.order) || 0,
            };
            if (editTarget) {
                await updateSocialLink(token, editTarget._id, payload);
                toast.success('Social link updated');
            } else {
                await createSocialLink(token, payload);
                toast.success('Social link created');
            }
            closeModal();
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    const handleToggle = async (link) => {
        try {
            await toggleSocialLink(token, link._id);
            setLinks(prev => prev.map(l => l._id === link._id ? { ...l, isActive: !l.isActive } : l));
        } catch {
            toast.error('Failed to toggle status');
        }
    };

    const handleDelete = async (id) => {
        setDeleting(id);
        try {
            await deleteSocialLink(token, id);
            toast.success('Social link deleted');
            setLinks(prev => prev.filter(l => l._id !== id));
        } catch {
            toast.error('Failed to delete');
        } finally {
            setDeleting(null);
            setShowDeleteConfirm(null);
        }
    };

    return (
        <div className="container-fluid py-4 px-3 px-md-4">
            <ToastContainer position="top-right" autoClose={3000} />

            {/* Header */}
            <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
                <div>
                    <h2 className="fw-bold mb-0">Social Media</h2>
                    <p className="text-muted small mb-0">Manage social media links shown on the frontend</p>
                </div>
                <button className="btn btn-primary" onClick={openCreate}>
                    + Add Link
                </button>
            </div>

            {/* Stats */}
            <div className="row g-3 mb-4">
                <div className="col-6 col-md-3">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                            <p className="text-muted small mb-1 text-uppercase fw-semibold">Total</p>
                            <h3 className="fw-bold text-primary mb-0">{links.length}</h3>
                        </div>
                    </div>
                </div>
                <div className="col-6 col-md-3">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                            <p className="text-muted small mb-1 text-uppercase fw-semibold">Active</p>
                            <h3 className="fw-bold text-success mb-0">{links.filter(l => l.isActive).length}</h3>
                        </div>
                    </div>
                </div>
                <div className="col-6 col-md-3">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                            <p className="text-muted small mb-1 text-uppercase fw-semibold">Inactive</p>
                            <h3 className="fw-bold text-secondary mb-0">{links.filter(l => !l.isActive).length}</h3>
                        </div>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="card border-0 shadow-sm">
                <div className="card-body p-0">
                    {loading ? (
                        <div className="d-flex justify-content-center align-items-center py-5">
                            <div className="spinner-border text-primary me-2" role="status"></div>
                            <span className="text-muted">Loading...</span>
                        </div>
                    ) : links.length === 0 ? (
                        <div className="text-center py-5">
                            <div className="fs-1 mb-2">📱</div>
                            <p className="text-muted fw-semibold mb-1">No social links yet</p>
                            <p className="text-muted small">Click "Add Link" to create your first social media link.</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th className="ps-3">Order</th>
                                        <th>Platform</th>
                                        <th>Name</th>
                                        <th>Icon Preview</th>
                                        <th>URL</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {links.map(link => (
                                        <tr key={link._id}>
                                            <td className="ps-3 text-muted small">{link.order ?? 0}</td>
                                            <td className="fw-semibold">{link.platform}</td>
                                            <td className="text-muted small">{link.name || <span className="text-muted fst-italic">—</span>}</td>
                                            <td>
                                                {link.icon ? (
                                                    <div
                                                        style={{ width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                        dangerouslySetInnerHTML={{ __html: link.icon }}
                                                    />
                                                ) : (
                                                    <span className="text-muted fst-italic small">No icon</span>
                                                )}
                                            </td>
                                            <td>
                                                <a href={link.url} target="_blank" rel="noreferrer" className="text-decoration-none small text-truncate d-inline-block" style={{ maxWidth: 180 }}>
                                                    {link.url}
                                                </a>
                                            </td>
                                            <td>
                                                <span className={`badge ${link.isActive ? 'bg-success' : 'bg-secondary'}`}>
                                                    {link.isActive ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="d-flex gap-1 flex-wrap">
                                                    <button className="btn btn-sm btn-outline-primary" onClick={() => openEdit(link)}>
                                                        Edit
                                                    </button>
                                                    <button
                                                        className={`btn btn-sm ${link.isActive ? 'btn-outline-secondary' : 'btn-outline-success'}`}
                                                        onClick={() => handleToggle(link)}
                                                    >
                                                        {link.isActive ? 'Disable' : 'Enable'}
                                                    </button>
                                                    <button
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={() => setShowDeleteConfirm(link._id)}
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Add / Edit Modal */}
            {showModal && (
                <>
                    <div className="modal fade show d-block" tabIndex="-1" role="dialog">
                        <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                            <div className="modal-content">
                                <form onSubmit={handleSave}>
                                    <div className="modal-header">
                                        <h5 className="modal-title fw-bold">
                                            {editTarget ? 'Edit Social Link' : 'Add Social Link'}
                                        </h5>
                                        <button type="button" className="btn-close" onClick={closeModal}></button>
                                    </div>
                                    <div className="modal-body">
                                        <div className="row g-3">
                                            <div className="col-md-6">
                                                <label className="form-label fw-semibold">
                                                    Platform <span className="text-danger">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name="platform"
                                                    value={form.platform}
                                                    onChange={handleChange}
                                                    placeholder="e.g. telegram, facebook, instagram"
                                                    required
                                                />
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label fw-semibold">
                                                    Name <span className="text-muted fw-normal">(optional)</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name="name"
                                                    value={form.name}
                                                    onChange={handleChange}
                                                    placeholder="Display name override"
                                                />
                                            </div>
                                            <div className="col-12">
                                                <label className="form-label fw-semibold">
                                                    URL <span className="text-danger">*</span>
                                                </label>
                                                <input
                                                    type="url"
                                                    className="form-control"
                                                    name="url"
                                                    value={form.url}
                                                    onChange={handleChange}
                                                    placeholder="https://t.me/yourgroup"
                                                    required
                                                />
                                            </div>
                                            <div className="col-12">
                                                <label className="form-label fw-semibold">
                                                    SVG Icon <span className="text-muted fw-normal">(optional — paste raw SVG markup)</span>
                                                </label>
                                                <textarea
                                                    className="form-control font-monospace"
                                                    name="icon"
                                                    value={form.icon}
                                                    onChange={handleChange}
                                                    rows={4}
                                                    placeholder='<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">...</svg>'
                                                />
                                                {form.icon && (
                                                    <div className="mt-2 d-flex align-items-center gap-2">
                                                        <span className="text-muted small">Preview:</span>
                                                        <div
                                                            style={{ width: 32, height: 32 }}
                                                            dangerouslySetInnerHTML={{ __html: form.icon }}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label fw-semibold">Order</label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    name="order"
                                                    value={form.order}
                                                    onChange={handleChange}
                                                    min={0}
                                                />
                                                <div className="form-text">Lower number = shown first</div>
                                            </div>
                                            <div className="col-md-6 d-flex align-items-center pt-4">
                                                <div className="form-check form-switch">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        role="switch"
                                                        id="isActiveSwitch"
                                                        name="isActive"
                                                        checked={form.isActive}
                                                        onChange={handleChange}
                                                    />
                                                    <label className="form-check-label fw-semibold" htmlFor="isActiveSwitch">
                                                        Active (visible on frontend)
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="modal-footer">
                                        <button type="button" className="btn btn-secondary" onClick={closeModal}>
                                            Cancel
                                        </button>
                                        <button type="submit" className="btn btn-primary" disabled={saving}>
                                            {saving ? (
                                                <><span className="spinner-border spinner-border-sm me-1" role="status"></span> Saving...</>
                                            ) : (
                                                editTarget ? 'Save Changes' : 'Create Link'
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                    <div className="modal-backdrop fade show"></div>
                </>
            )}

            {/* Delete Confirm Modal */}
            {showDeleteConfirm && (
                <>
                    <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ zIndex: 1060 }}>
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title fw-bold text-danger">Delete Social Link</h5>
                                    <button type="button" className="btn-close" onClick={() => setShowDeleteConfirm(null)}></button>
                                </div>
                                <div className="modal-body">
                                    <p className="mb-0">Are you sure you want to delete this social link? This cannot be undone.</p>
                                </div>
                                <div className="modal-footer">
                                    <button className="btn btn-secondary" onClick={() => setShowDeleteConfirm(null)}>
                                        Cancel
                                    </button>
                                    <button
                                        className="btn btn-danger"
                                        onClick={() => handleDelete(showDeleteConfirm)}
                                        disabled={deleting === showDeleteConfirm}
                                    >
                                        {deleting === showDeleteConfirm ? (
                                            <><span className="spinner-border spinner-border-sm me-1" role="status"></span> Deleting...</>
                                        ) : 'Yes, Delete'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-backdrop fade show" style={{ zIndex: 1055 }}></div>
                </>
            )}
        </div>
    );
};

export default SocialMedia;